import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Export report data to PDF using jsPDF (no screenshot).
 * @param {Object} options
 * @param {string} options.title - Report title
 * @param {string} options.companyName - Company name
 * @param {string} options.period - Period text e.g. "01/01/2024 – 31/01/2024"
 * @param {Array<{id: string, label: string}>} options.columns
 * @param {Array<Object>} options.rows - Array of row objects keyed by column id
 * @param {Array<{label: string, value: string}>} options.summaryItems
 * @param {string} [options.filename] - Download filename without extension
 */
export function exportReportToPdf({
  title,
  companyName,
  period,
  columns = [],
  rows = [],
  summaryItems = [],
  filename = 'report',
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 15;

  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(title || 'Report', 14, y);
  y += 8;

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  if (companyName) {
    doc.text(companyName, 14, y);
    y += 5;
  }
  if (period) {
    doc.text(`Period: ${period}`, 14, y);
    y += 8;
  }

  if (summaryItems.length > 0) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    const summaryText = summaryItems.map((s) => `${s.label}: ${s.value}`).join('  |  ');
    const lines = doc.splitTextToSize(summaryText, pageWidth - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 8;
  }

  if (columns.length > 0 && rows.length > 0) {
    const headers = columns.map((c) => c.label);
    const tableData = rows.map((row) => columns.map((col) => {
      const v = row[col.id];
      if (v == null || v === '') return '–';
      if (typeof v === 'number' && !Number.isInteger(v)) return Number(v).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2));
      return String(v);
    }));

    autoTable(doc, {
      startY: y,
      head: [headers],
      body: tableData,
      margin: { left: 14, right: 14 },
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      tableLineColor: [200, 200, 200],
      tableLineWidth: 0.1,
    });
  }

  const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y;
  if (finalY > doc.internal.pageSize.getHeight() - 15) {
    doc.addPage('a4', 'landscape');
  }

  doc.save(`${filename}.pdf`);
}

/**
 * Export report data to Excel (.xlsx).
 * @param {Object} options - Same shape as exportReportToPdf
 * @param {string} [options.sheetName='Report'] - Sheet name (max 31 chars)
 */
export function exportReportToExcel({
  title,
  companyName,
  period,
  columns = [],
  rows = [],
  summaryItems = [],
  filename = 'report',
  sheetName = 'Report',
}) {
  const wb = XLSX.utils.book_new();

  const wsData = [];

  if (title) wsData.push([title]);
  if (companyName) wsData.push([companyName]);
  if (period) wsData.push([`Period: ${period}`]);
  wsData.push([]);

  if (summaryItems.length > 0) {
    wsData.push(['Summary']);
    summaryItems.forEach((s) => wsData.push([s.label, s.value]));
    wsData.push([]);
  }

  if (columns.length > 0) {
    wsData.push(columns.map((c) => c.label));
    rows.forEach((row) => {
      wsData.push(columns.map((col) => {
        const v = row[col.id];
        if (v == null || v === '') return '';
        if (typeof v === 'number') return v;
        return String(v);
      }));
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = columns.length
    ? columns.map((_, i) => {
        const maxLen = Math.max(
          (columns[i].label || '').length,
          ...rows.map((r) => String(r[columns[i].id] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 1, 10), 50) };
      })
    : [{ wch: 20 }];
  ws['!cols'] = colWidths;

  const sheetNameSafe = (sheetName || 'Report').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetNameSafe);
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
